import { useEffect, useRef } from 'react';

/**
 * Custom hook that adds smooth 3D perspective tilt effect to an element.
 * Updates CSS variables: --card-rotate-x, --card-rotate-y, --mouse-x, --mouse-y
 *
 * @param {number} maxTilt - Maximum tilt angle in degrees (default: 6)
 * @returns {React.RefObject} - Ref to attach to the target element
 */
export default function useTiltEffect(maxTilt = 2) {
  const elementRef = useRef(null);

  useEffect(() => {
    const el = elementRef.current;
    if (!el) return;

    // Check for touch/coarse devices and reduced motion preference
    if (
      typeof window !== 'undefined' &&
      (window.matchMedia('(hover: none)').matches ||
       window.matchMedia('(prefers-reduced-motion: reduce)').matches)
    ) {
      return;
    }

    let animationFrameId = null;

    const handleMouseMove = (e) => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);

      animationFrameId = requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Normalized offsets between -1 and 1
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        const percentX = (x - centerX) / centerX;
        const percentY = (y - centerY) / centerY;

        // Calculate rotation angles
        const rotateX = -(percentY * maxTilt);
        const rotateY = percentX * maxTilt;

        el.style.setProperty('--card-rotate-x', `${rotateX.toFixed(2)}deg`);
        el.style.setProperty('--card-rotate-y', `${rotateY.toFixed(2)}deg`);
        el.style.setProperty('--mouse-x', `${x.toFixed(1)}px`);
        el.style.setProperty('--mouse-y', `${y.toFixed(1)}px`);
      });
    };

    const handleMouseLeave = () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);

      animationFrameId = requestAnimationFrame(() => {
        el.style.setProperty('--card-rotate-x', '0deg');
        el.style.setProperty('--card-rotate-y', '0deg');
      });
    };

    el.addEventListener('mousemove', handleMouseMove, { passive: true });
    el.addEventListener('mouseleave', handleMouseLeave, { passive: true });

    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
      el.removeEventListener('mousemove', handleMouseMove);
      el.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [maxTilt]);

  return elementRef;
}
